import { useState } from 'react';

// ─────────────────────────────────────────────────────────────────
// AffiliatePage — standalone public route (/affiliate)
// Dark, no auth, no sidebar. Leads with the pay structure, then the
// application form. On submit, fires in parallel:
//   1. Google Forms (no-cors, fire-and-forget — source of truth)
//   2. Supabase via /api/affiliate (best-effort persistence + founder email)
//   3. Confirmation screen replaces the form.
// ─────────────────────────────────────────────────────────────────

const GOOGLE_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSeQJq4gE6D5h9kPRaxytYK6_OLT23Nuqr9cNn3skkGHT4qlPg/formResponse';

const ENTRY = {
  name: 'entry.276188699',
  email: 'entry.2013044836',
  phone: 'entry.701951452',
  brandName: 'entry.1683402519',
  location: 'entry.192291015',
  contactMethods: 'entry.2026057145',
  textConsent: 'entry.1356774422',
  channels: 'entry.1386646541',
  audienceSize: 'entry.1732993390',
  socialLinks: 'entry.808275291',
  explanation: 'entry.198593803',
  promotedBefore: 'entry.342080089',
  startTimeline: 'entry.1088790323',
  frequency: 'entry.2006933209',
  contentKit: 'entry.1260382628',
  liveDemos: 'entry.1503850256',
  payoutAgreement: 'entry.1191599128',
  termsAgreement: 'entry.986000577',
  conductAgreement: 'entry.853079477',
};

const CONTACT_METHODS = ['Email', 'Text', 'Phone call', 'Discord', 'Other'];
const CHANNELS = ['TikTok', 'YouTube', 'Instagram', 'Facebook', 'LinkedIn', 'Reddit', 'Discord', 'Email list', 'Website/blog', 'In-person/community events', 'Other'];
const AUDIENCE_SIZE = ['Under 500', '500–2,500', '2,500–10,000', '10,000–50,000', '50,000+'];
const PROMOTED_BEFORE = ['Yes', 'No', 'Not officially, but I have an audience/community'];
const START_TIMELINE = ['Immediately', 'Within 1 week', 'Within 2–4 weeks', 'Not sure yet'];
const FREQUENCY = ['One-time post', 'Daily', 'Multiple times per week', 'Weekly', 'Figuring it out as I go'];
const YES_NO = ['Yes', 'No'];
const YES_NO_MAYBE = ['Yes', 'No', 'Maybe'];

const TEXT_CONSENT_LABEL =
  'I agree to receive text messages from AdaptOS regarding affiliate on-boarding, referral links, payout updates, and partner communications. Reply STOP to opt out.';
const PAYOUT_BLOCK =
  'All affiliate payouts are processed securely through Stripe Direct Deposit. Approved affiliates receive onboarding instructions to connect their bank account through Stripe after acceptance. Payments issued semi-monthly based on active paying referrals. To remain eligible: referrals must be active paying users, traffic must be legitimate, affiliates must follow AdaptOS promotional guidelines.';
const TERMS_LABEL =
  'I understand that AdaptOS affiliate approval is not automatic. Approved affiliates will receive a unique referral link or code. Affiliate commissions are paid only for active, paying users referred through my assigned link/code, subject to AdaptOS affiliate terms.';
const CONDUCT_LABEL =
  'I agree not to make false income claims, guaranteed job claims, guaranteed certification-pass claims, or misleading promises when promoting AdaptOS.';

const PAY_EXAMPLES = [
  ['284 users', '$1,420/month'],
  ['200 users', '$1,000/month'],
  ['500 users', '$2,500/month'],
];

// ── Reusable selectable groups ────────────────────────────────────
function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="af-opts">
      {options.map((opt) => (
        <label key={opt} className={`af-opt${value === opt ? ' af-opt-on' : ''}`}>
          <input type="radio" name={name} value={opt} checked={value === opt} onChange={() => onChange(opt)} />
          <span className="af-opt-box" aria-hidden />
          <span className="af-opt-text">{opt}</span>
        </label>
      ))}
    </div>
  );
}

function CheckGroup({ options, values, onToggle }) {
  return (
    <div className="af-opts">
      {options.map((opt) => {
        const on = values.includes(opt);
        return (
          <label key={opt} className={`af-opt${on ? ' af-opt-on' : ''}`}>
            <input type="checkbox" checked={on} onChange={() => onToggle(opt)} />
            <span className="af-opt-box af-opt-box-sq" aria-hidden />
            <span className="af-opt-text">{opt}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function AffiliatePage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [brandName, setBrandName] = useState('');
  const [location, setLocation] = useState('');
  const [contactMethods, setContactMethods] = useState([]);
  const [textConsent, setTextConsent] = useState('');
  const [channels, setChannels] = useState([]);
  const [audienceSize, setAudienceSize] = useState('');
  const [socialLinks, setSocialLinks] = useState('');
  const [explanation, setExplanation] = useState('');
  const [promotedBefore, setPromotedBefore] = useState('');
  const [startTimeline, setStartTimeline] = useState('');
  const [frequency, setFrequency] = useState('');
  const [contentKit, setContentKit] = useState('');
  const [liveDemos, setLiveDemos] = useState('');
  const [payoutAgreement, setPayoutAgreement] = useState('');
  const [termsAgreement, setTermsAgreement] = useState('');
  const [conductAgreement, setConductAgreement] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const toggle = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const missing = [];
    if (!name.trim()) missing.push('Full Name');
    if (!email.trim()) missing.push('Email Address');
    if (!phone.trim()) missing.push('Phone Number');
    if (!location.trim()) missing.push('City/State');
    if (contactMethods.length === 0) missing.push('Preferred contact method');
    if (!textConsent) missing.push('Text consent');
    if (channels.length === 0) missing.push('Where to promote');
    if (!audienceSize) missing.push('Audience size');
    if (!promotedBefore) missing.push('Promoted before');
    if (!startTimeline) missing.push('Start timeline');
    if (!frequency) missing.push('Promotion frequency');
    if (!contentKit) missing.push('Content kit');
    if (!payoutAgreement) missing.push('Payout agreement');
    if (!termsAgreement) missing.push('Affiliate terms');
    if (!conductAgreement) missing.push('Brand conduct');
    if (missing.length) {
      setError(`Please complete: ${missing.join(', ')}.`);
      return;
    }

    setSubmitting(true);

    // 1 — Google Forms (fire and forget)
    // NOTE: must be url-encoded, not multipart/FormData. Google's
    // /formResponse endpoint records a response from a multipart body but
    // fails to parse the fields, producing a row with all answers blank.
    try {
      const params = new URLSearchParams();
      params.append(ENTRY.name, name);
      params.append(ENTRY.email, email);
      params.append(ENTRY.phone, phone);
      params.append(ENTRY.brandName, brandName);
      params.append(ENTRY.location, location);
      contactMethods.forEach((m) => params.append(ENTRY.contactMethods, m));
      params.append(ENTRY.textConsent, textConsent);
      channels.forEach((c) => params.append(ENTRY.channels, c));
      params.append(ENTRY.audienceSize, audienceSize);
      params.append(ENTRY.socialLinks, socialLinks);
      params.append(ENTRY.explanation, explanation);
      params.append(ENTRY.promotedBefore, promotedBefore);
      params.append(ENTRY.startTimeline, startTimeline);
      params.append(ENTRY.frequency, frequency);
      params.append(ENTRY.contentKit, contentKit);
      params.append(ENTRY.liveDemos, liveDemos);
      params.append(ENTRY.payoutAgreement, payoutAgreement);
      params.append(ENTRY.termsAgreement, termsAgreement);
      params.append(ENTRY.conductAgreement, conductAgreement);
      // Multi-section form (7 sections → pages 0–6): without pageHistory, Google
      // records the response with every answer blank. fvv/submissionTimestamp
      // mirror what the real form sends.
      params.append('fvv', '1');
      params.append('pageHistory', '0,1,2,3,4,5,6');
      params.append('submissionTimestamp', '-1');
      fetch(GOOGLE_FORM_URL, { method: 'POST', mode: 'no-cors', body: params });
    } catch { /* ignore */ }

    // 2 — Supabase via server route
    try {
      await fetch('/api/affiliate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          brand_name: brandName,
          location,
          contact_methods: contactMethods,
          text_consent: textConsent,
          promotion_channels: channels,
          audience_size: audienceSize,
          social_links: socialLinks,
          explanation,
          promoted_before: promotedBefore,
          start_timeline: startTimeline,
          frequency,
          content_kit: contentKit,
          live_demos: liveDemos,
          status: 'pending',
        }),
      });
    } catch { /* ignore */ }

    setSubmitting(false);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="af-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300..600&family=Hanken+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .af-root { background:#0f0f12; color:#f4f4f5; min-height:100vh; font-family:'Hanken Grotesk',sans-serif; position:relative; overflow-x:hidden; }
        .af-root * { box-sizing:border-box; }
        .af-root ::selection { background:#2dd4bf; color:#0f0f12; }
        .af-glow { position:fixed; top:-25%; left:50%; transform:translateX(-50%); width:min(900px,120vw); height:640px; background:radial-gradient(ellipse at center, rgba(45,212,191,0.14) 0%, transparent 65%); filter:blur(20px); pointer-events:none; z-index:0; }
        .af-wrap { position:relative; z-index:1; max-width:820px; margin:0 auto; padding:clamp(8px,2vw,18px) clamp(20px,5vw,40px) clamp(44px,7vw,96px); }
        .af-brand { position:relative; z-index:1; max-width:820px; margin:0 auto; padding:clamp(24px,4vw,36px) clamp(20px,5vw,40px) 0; }
        .af-brand img { height:54px; width:auto; object-fit:contain; border-radius:12px; display:block; }

        .af-eyebrow { font-family:'IBM Plex Mono',monospace; font-size:12px; letter-spacing:0.22em; text-transform:uppercase; color:#2dd4bf; margin-bottom:22px; }
        .af-h1 { font-family:'Fraunces',serif; font-weight:400; font-size:clamp(30px,5vw,52px); line-height:1.08; letter-spacing:-0.02em; margin:0 0 24px; }
        .af-lead { font-size:clamp(17px,2.2vw,22px); line-height:1.45; color:#f4f4f5; font-weight:400; margin:0 0 12px; max-width:46ch; }
        .af-lead b { color:#2dd4bf; }
        .af-sub { font-size:clamp(15px,1.8vw,17px); line-height:1.6; color:#9a9aa4; margin:0; max-width:56ch; font-weight:300; }

        .af-cards { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin:34px 0; }
        .af-card { background:linear-gradient(rgba(45,212,191,0.07),rgba(45,212,191,0.07)),#15151b; border:1px solid rgba(45,212,191,0.25); border-radius:16px; padding:24px 20px; text-align:center; }
        .af-card-u { font-family:'IBM Plex Mono',monospace; font-size:13px; color:#9a9aa4; letter-spacing:0.04em; margin-bottom:10px; }
        .af-card-arrow { color:#6a6a73; font-size:13px; }
        .af-card-amt { font-family:'Fraunces',serif; font-size:clamp(22px,3.4vw,30px); color:#2dd4bf; line-height:1.05; margin-top:8px; }

        .af-pitch p { font-size:clamp(15px,1.8vw,17px); line-height:1.65; color:#c4c4cc; margin:0 0 10px; max-width:60ch; font-weight:300; }
        .af-apply { font-family:'IBM Plex Mono',monospace; font-size:13px; letter-spacing:0.16em; text-transform:uppercase; color:#2dd4bf; margin-top:24px; }

        .af-strip { margin-top:30px; background:rgba(0,0,0,0.3); border:1px solid rgba(255,255,255,0.08); border-left:3px solid #2dd4bf; border-radius:12px; padding:16px 18px; font-size:13.5px; line-height:1.6; color:#9a9aa4; }
        .af-strip b { color:#f4f4f5; font-weight:600; }

        .af-form { margin-top:clamp(38px,6vw,56px); display:flex; flex-direction:column; gap:32px; }
        .af-section-h { font-family:'IBM Plex Mono',monospace; font-size:12px; letter-spacing:0.18em; text-transform:uppercase; color:#2dd4bf; padding-top:14px; border-top:1px solid rgba(255,255,255,0.08); }
        .af-field { display:flex; flex-direction:column; gap:13px; }
        .af-label { font-size:15.5px; font-weight:600; color:#f4f4f5; line-height:1.45; }
        .af-label-sm { font-weight:400; color:#6a6a73; }
        .af-req { color:#2dd4bf; margin-left:5px; }
        .af-consent-text { font-size:13.5px; line-height:1.6; color:#9a9aa4; }
        .af-block { background:rgba(0,0,0,0.3); border:1px solid rgba(255,255,255,0.08); border-radius:12px; padding:16px 18px; font-size:13.5px; line-height:1.6; color:#9a9aa4; }

        .af-input, .af-textarea { width:100%; background:#15151b; border:1px solid rgba(255,255,255,0.1); border-radius:11px; padding:14px 15px; color:#f4f4f5; font-family:inherit; font-size:15px; transition:border-color .2s, background .2s; }
        .af-input:focus, .af-textarea:focus { outline:none; border-color:#2dd4bf; background:#191920; }
        .af-input::placeholder, .af-textarea::placeholder { color:#5a5a63; }
        .af-textarea { min-height:104px; resize:vertical; line-height:1.5; }

        .af-opts { display:flex; flex-direction:column; gap:10px; }
        .af-opt { position:relative; display:flex; align-items:flex-start; gap:13px; background:#15151b; border:1px solid rgba(255,255,255,0.1); border-radius:11px; padding:14px 16px; cursor:pointer; transition:border-color .2s, background .2s; }
        .af-opt:hover { border-color:rgba(45,212,191,0.4); }
        .af-opt-on { border-color:#2dd4bf; background:linear-gradient(rgba(45,212,191,0.10),rgba(45,212,191,0.10)),#15151b; }
        .af-opt input { position:absolute; opacity:0; width:1px; height:1px; margin:0; }
        .af-opt-box { flex:0 0 auto; width:20px; height:20px; border-radius:50%; border:2px solid #4a4a54; margin-top:1px; display:grid; place-items:center; transition:border-color .2s; }
        .af-opt-box-sq { border-radius:6px; }
        .af-opt-on .af-opt-box { border-color:#2dd4bf; }
        .af-opt-on .af-opt-box::after { content:''; width:10px; height:10px; border-radius:inherit; background:#2dd4bf; }
        .af-opt-text { font-size:14.5px; line-height:1.5; color:#d4d4d8; }
        .af-opt:focus-within { border-color:#2dd4bf; }

        .af-error { background:rgba(248,113,113,0.10); border:1px solid rgba(248,113,113,0.4); color:#fca5a5; border-radius:10px; padding:13px 15px; font-size:14px; line-height:1.5; }
        .af-submit { width:100%; background:#2dd4bf; color:#0f0f12; border:none; border-radius:999px; padding:17px; font-family:inherit; font-size:16px; font-weight:700; cursor:pointer; transition:transform .2s, box-shadow .2s, opacity .2s; box-shadow:0 10px 30px rgba(45,212,191,0.28); }
        .af-submit:hover { transform:translateY(-2px); box-shadow:0 16px 40px rgba(45,212,191,0.42); }
        .af-submit:disabled { opacity:0.6; cursor:default; transform:none; box-shadow:none; }

        .af-done { position:relative; z-index:1; min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:40px 24px; }
        .af-done-h { font-family:'Fraunces',serif; font-weight:400; font-size:clamp(40px,8vw,72px); margin:0 0 26px; color:#2dd4bf; letter-spacing:-0.02em; }
        .af-done p { font-size:clamp(16px,2vw,19px); color:#9a9aa4; line-height:1.6; margin:0 0 14px; max-width:46ch; font-weight:300; }

        @media (max-width:640px) {
          .af-cards { grid-template-columns:1fr; }
          .af-opt-text { font-size:14px; }
        }
      `}</style>

      <div className="af-glow" aria-hidden />

      <div className="af-brand">
        <a href="https://adaptos.app" target="_blank" rel="noopener noreferrer">
          <img src="/logo.jpg" alt="AdaptOS" />
        </a>
      </div>

      {submitted ? (
        <div className="af-done">
          <h1 className="af-done-h">Application received.</h1>
          <p>If approved, you&apos;ll receive your affiliate kit, referral details, messaging guide, and next steps within 48 hours.</p>
          <p>We&apos;re looking for partners who care about helping learners find a real path forward — not just clicks.</p>
        </div>
      ) : (
        <div className="af-wrap">
          {/* ── PAY STRUCTURE ── */}
          <div className="af-eyebrow">AdaptOS Affiliate Partner Program</div>
          <h1 className="af-h1">Earn $5 per active paying user, every month.</h1>
          <p className="af-lead"><b>$5</b> per active paying user per month.</p>
          <p className="af-sub">Payments are semi-monthly. Commission continues as long as your referral stays subscribed.</p>

          <div className="af-cards">
            {PAY_EXAMPLES.map(([users, amt]) => (
              <div className="af-card" key={users}>
                <div className="af-card-u">{users} <span className="af-card-arrow">→</span></div>
                <div className="af-card-amt">{amt}</div>
              </div>
            ))}
          </div>

          <div className="af-pitch">
            <p>You don&apos;t need to build a course, manage students, or handle support.</p>
            <p>Promote from your phone with your unique affiliate code.</p>
            <p>We provide approved videos, messaging, graphics, and talking points.</p>
            <div className="af-apply">Apply below ↓</div>
          </div>

          <div className="af-strip">
            All payouts processed via <b>Stripe Direct Deposit</b>. Semi-monthly based on active paying referrals.
            Referrals must remain active paying subscribers. <b>DO NOT submit SSN, tax ID, or bank details here</b> —
            that&apos;s handled securely during onboarding.
          </div>

          {/* ── FORM ── */}
          <form className="af-form" onSubmit={handleSubmit} noValidate>
            <div className="af-section-h">Basic Info</div>

            <div className="af-field">
              <label className="af-label" htmlFor="af-name">Full Name<span className="af-req">*</span></label>
              <input id="af-name" className="af-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />
            </div>
            <div className="af-field">
              <label className="af-label" htmlFor="af-email">Email Address<span className="af-req">*</span></label>
              <input id="af-email" className="af-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
            </div>
            <div className="af-field">
              <label className="af-label" htmlFor="af-phone">Phone Number<span className="af-req">*</span></label>
              <input id="af-phone" className="af-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" />
            </div>
            <div className="af-field">
              <label className="af-label" htmlFor="af-brand">Business/Brand Name</label>
              <input id="af-brand" className="af-input" type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Optional" />
            </div>
            <div className="af-field">
              <label className="af-label" htmlFor="af-loc">City/State<span className="af-req">*</span></label>
              <input id="af-loc" className="af-input" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, State" />
            </div>
            <div className="af-field">
              <span className="af-label">Preferred contact method <span className="af-label-sm">(select all that apply)</span><span className="af-req">*</span></span>
              <CheckGroup options={CONTACT_METHODS} values={contactMethods} onToggle={(v) => toggle(contactMethods, setContactMethods, v)} />
            </div>
            <div className="af-field">
              <span className="af-label">Text consent<span className="af-req">*</span></span>
              <div className="af-consent-text">{TEXT_CONSENT_LABEL}</div>
              <RadioGroup name="textConsent" options={['I Agree', 'Opt-out']} value={textConsent} onChange={setTextConsent} />
            </div>

            <div className="af-section-h">Audience &amp; Channels</div>

            <div className="af-field">
              <span className="af-label">Where to promote <span className="af-label-sm">(select all that apply)</span><span className="af-req">*</span></span>
              <CheckGroup options={CHANNELS} values={channels} onToggle={(v) => toggle(channels, setChannels, v)} />
            </div>
            <div className="af-field">
              <span className="af-label">Audience size<span className="af-req">*</span></span>
              <RadioGroup name="audienceSize" options={AUDIENCE_SIZE} value={audienceSize} onChange={setAudienceSize} />
            </div>
            <div className="af-field">
              <label className="af-label" htmlFor="af-links">Links to your main pages/channels</label>
              <input id="af-links" className="af-input" type="text" value={socialLinks} onChange={(e) => setSocialLinks(e.target.value)} placeholder="Optional — paste your links" />
            </div>

            <div className="af-section-h">Fit Check</div>

            <div className="af-field">
              <label className="af-label" htmlFor="af-explain">How would you explain AdaptOS to your audience?</label>
              <textarea id="af-explain" className="af-textarea" value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="Optional — in your own words." />
            </div>
            <div className="af-field">
              <span className="af-label">Have you promoted products/services before?<span className="af-req">*</span></span>
              <RadioGroup name="promotedBefore" options={PROMOTED_BEFORE} value={promotedBefore} onChange={setPromotedBefore} />
            </div>

            <div className="af-section-h">Promotion Plan</div>

            <div className="af-field">
              <span className="af-label">How soon could you start after approval?<span className="af-req">*</span></span>
              <RadioGroup name="startTimeline" options={START_TIMELINE} value={startTimeline} onChange={setStartTimeline} />
            </div>
            <div className="af-field">
              <span className="af-label">How often do you expect to promote?<span className="af-req">*</span></span>
              <RadioGroup name="frequency" options={FREQUENCY} value={frequency} onChange={setFrequency} />
            </div>
            <div className="af-field">
              <span className="af-label">Open to receiving a content kit?<span className="af-req">*</span></span>
              <RadioGroup name="contentKit" options={YES_NO} value={contentKit} onChange={setContentKit} />
            </div>
            <div className="af-field">
              <span className="af-label">Open to live demos or affiliate training?</span>
              <RadioGroup name="liveDemos" options={YES_NO_MAYBE} value={liveDemos} onChange={setLiveDemos} />
            </div>

            <div className="af-section-h">Payout &amp; Agreement</div>

            <div className="af-block">{PAYOUT_BLOCK}</div>

            <div className="af-field">
              <span className="af-label">Payout agreement<span className="af-req">*</span></span>
              <RadioGroup name="payoutAgreement" options={['I understand and agree']} value={payoutAgreement} onChange={setPayoutAgreement} />
            </div>
            <div className="af-field">
              <span className="af-label">Affiliate terms<span className="af-req">*</span></span>
              <div className="af-consent-text">{TERMS_LABEL}</div>
              <RadioGroup name="termsAgreement" options={['I understand.']} value={termsAgreement} onChange={setTermsAgreement} />
            </div>
            <div className="af-field">
              <span className="af-label">Brand conduct<span className="af-req">*</span></span>
              <div className="af-consent-text">{CONDUCT_LABEL}</div>
              <RadioGroup name="conductAgreement" options={['I agree.']} value={conductAgreement} onChange={setConductAgreement} />
            </div>

            {error && <div className="af-error">{error}</div>}

            <button type="submit" className="af-submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Apply to Become an Affiliate →'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
